/**
 * DiameterCompute — centerline frame resolution and diameter pipeline orchestration.
 *
 * Resolves a centerline frame (master or per-branch polydata variant), then
 * wires MaskReslice and rawmap-diameter-algo together into a single call.
 */

import { vec3 } from 'gl-matrix';
import { calculateDiameters } from 'rawmap-diameter-algo';

import { computeFrameAtIndex } from '../js/CenterlineUtils';
import {
    newReslice,
    runReslice,
    normalizeReslicedMaskToBinary,
    applyStoredSliceBuffer,
} from '../js/MaskReslice';

/**
 * Build a centerline frame from a polydata whose per-point `Normals` array
 * stores the negated tangent (master centerline convention — no Orientation
 * tensors).  Bitangent and normal are derived arbitrarily; rawmap-diameter-algo
 * is rotation-invariant within the resliced plane so the choice does not
 * affect scalar diameters.
 *
 * @param {import('vtk.js/Sources/Common/DataModel/PolyData').vtkPolyData} polyData
 * @param {number} masterIndex
 * @returns {{ T: import('gl-matrix').vec3, B: import('gl-matrix').vec3, N: import('gl-matrix').vec3, P: Float32Array } | null}
 */
function computeFrameFromMasterPolyData(polyData, masterIndex) {
    if (!polyData) return null;

    const points = polyData.getPoints();
    const numPoints = points?.getNumberOfPoints?.() ?? 0;
    if (numPoints === 0) return null;

    // Reject the "no selection" sentinel (-1) and any out-of-range index
    if (masterIndex < 0 || masterIndex >= numPoints) return null;

    const idx = masterIndex;
    const pts = points.getData();
    const P = pts.subarray(3 * idx, 3 * idx + 3);

    const normalsArray = polyData.getPointData().getArrayByName('Normals');
    const T = vec3.create();

    if (normalsArray) {
        // VTP convention: stored normal = -tangent.  See CenterlineUtils.js.
        const normals = normalsArray.getData();
        vec3.set(T, -normals[3 * idx], -normals[3 * idx + 1], -normals[3 * idx + 2]);
    }

    if (vec3.len(T) < 1e-12) {
        if (numPoints <= 1) {
            vec3.set(T, 0, 0, 1);
        } else if (idx === 0) {
            vec3.subtract(T, pts.subarray(3, 6), pts.subarray(0, 3));
        } else if (idx === numPoints - 1) {
            vec3.subtract(T,
                pts.subarray(3 * idx, 3 * idx + 3),
                pts.subarray(3 * (idx - 1), 3 * (idx - 1) + 3));
        } else {
            vec3.subtract(T,
                pts.subarray(3 * (idx + 1), 3 * (idx + 1) + 3),
                pts.subarray(3 * (idx - 1), 3 * (idx - 1) + 3));
        }
        if (vec3.len(T) < 1e-12) vec3.set(T, 0, 0, 1);
    }
    vec3.normalize(T, T);

    const B = vec3.create();
    vec3.set(B, 1, 0, 0);
    if (Math.abs(vec3.dot(B, T)) > 0.9) vec3.set(B, 0, 1, 0);
    const proj = vec3.scale(vec3.create(), T, vec3.dot(B, T));
    vec3.sub(B, B, proj);
    if (vec3.len(B) < 1e-8) {
        vec3.set(B, 0, 0, 1);
        const proj2 = vec3.scale(vec3.create(), T, vec3.dot(B, T));
        vec3.sub(B, B, proj2);
    }
    vec3.normalize(B, B);

    const N = vec3.cross(vec3.create(), T, B);
    vec3.normalize(N, N);

    return { T, B, N, P };
}

/** Returns true when `polyData` carries per-point Orientation tensors (per-branch variant). */
function hasOrientationTensors(polyData) {
    if (!polyData) return false;
    const tensors = polyData.getPointData().getTensors?.();
    return !!tensors;
}

/** Build a centerline frame at `index`, dispatching on the polydata variant (master vs. per-branch). */
export function frameAtIndex(polyData, index) {
    if (!polyData) return null;
    if (hasOrientationTensors(polyData)) {
        return computeFrameAtIndex(polyData, index);
    }
    return computeFrameFromMasterPolyData(polyData, index);
}

/**
 * Coerce rawmap-diameter-algo output into the F# CalculatedDiameter shape,
 * replacing any NaN fields with 0.
 *
 * @param {object} diameters - rawmap-diameter-algo output.
 * @param {number} index - centerline index (re-emitted on the result).
 * @returns {object} DiameterCalculator.CalculatedDiameter
 */
function sanitizeCalculatedDiameter(diameters, index) {
    const safe = (v) => (Number.isFinite(v) ? v : 0);
    return {
        stentPlanningMin: safe(diameters?.stentPlanningMin),
        stentPlanningMax: safe(diameters?.stentPlanningMax),
        stentPlanningAverage: safe(diameters?.stentPlanningAverage),
        AverageLine: diameters?.AverageLine ?? null,
        receivedType: diameters?.receivedType,
        index: typeof diameters?.index === 'number' ? diameters.index : index,
        centerWorld: diameters?.centerWorld ?? [0, 0, 0],
        imageInfo: diameters?.imageInfo ?? { dims: [0, 0, 0], spacing: [0, 0, 0], origin: [0, 0, 0] },
        samples: diameters?.samples ?? null,
    };
}

/**
 * Compute the stent-planning diameter at a centerline index.  Reslices the
 * given mask volume at the centerline frame, applies any stored painted slice
 * buffer for that index, and returns the rawmap-diameter-algo output.
 *
 * @param {{
 *   reslice?: import('vtk.js/Sources/Imaging/Core/ImageReslice').vtkImageReslice,
 *   maskVolume: import('vtk.js/Sources/Common/DataModel/ImageData').vtkImageData,
 *   polyData: import('vtk.js/Sources/Common/DataModel/PolyData').vtkPolyData,
 *   index: number,
 *   storedBuffer?: ArrayLike<number> | null
 * }} args
 */
export function computeDiameterAtIndex({ reslice, maskVolume, polyData, index, storedBuffer }) {
    if (!maskVolume || !polyData) return null;

    const frame = frameAtIndex(polyData, index);
    if (!frame) return null;

    const localReslice = reslice ?? newReslice();
    const resliced = runReslice(localReslice, maskVolume, frame);
    if (!resliced) return null;

    normalizeReslicedMaskToBinary(resliced);
    if (storedBuffer) {
        applyStoredSliceBuffer(resliced, storedBuffer);
    }

    const diameters = calculateDiameters(resliced, index);
    return sanitizeCalculatedDiameter(diameters, index);
}
